import {
	AlertCircle,
	CheckCircle,
	Edit05,
	InfoCircle,
	Mail01,
	Phone,
	RefreshCw01,
	XCircle,
} from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { Input } from "@/components/base/input/input";
import { TabHeader } from "@/components/base/tab-header";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAuth } from "@/contexts/AuthProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import {
	useRequestPasswordReset,
	useSendMfaVerificationCode,
	useUpdateMfa,
} from "@/services/user/user.mutations";

// Cooldown period in seconds before another password reset email can be sent
const PASSWORD_RESET_COOLDOWN_SECONDS = 60;

export interface ProfileSecurityContentProps {
	/** When provided, MFA edit state is controlled by parent (e.g. for tab header "Edit MFA Details") */
	isEditingMfa?: boolean;
	onEditingMfaChange?: (editing: boolean) => void;
}

export default function ProfileSecurityContent({
	isEditingMfa: isEditingMfaProp,
	onEditingMfaChange,
}: ProfileSecurityContentProps = {}) {
	const { t } = useAppTranslation();
	const { user, isLoading } = useAuth();

	// Password reset state
	const [resetEmailSent, setResetEmailSent] = useState(false);
	const [cooldownRemaining, setCooldownRemaining] = useState(0);

	// MFA state: use lifted state when provided, otherwise local
	const [isEditingMfaLocal, setIsEditingMfaLocal] = useState(false);
	const isEditingMfa = onEditingMfaChange
		? (isEditingMfaProp ?? false)
		: isEditingMfaLocal;
	const setIsEditingMfa = onEditingMfaChange
		? (value: boolean) => onEditingMfaChange(value)
		: setIsEditingMfaLocal;

	const [mfaEnabled, _setMfaEnabled] = useState(false); //! This will be implemented later per Ted's roadmap. The Feature is called "Organization MFA settings"
	const [mfaPhone, setMfaPhone] = useState("");
	const [mfaCodeSent, setMfaCodeSent] = useState(false);
	const [mfaVerificationCode, setMfaVerificationCode] = useState("");
	const [mfaResendCooldown, setMfaResendCooldown] = useState(0);
	const [mfaSubmitError, setMfaSubmitError] = useState<string | null>(null);

	const MFA_CODE_RESEND_COOLDOWN_SECONDS = 60;
	const mfaPhoneDigits = (mfaPhone ?? "").replace(/\D/g, "");
	const isMfaPhoneValid = mfaPhoneDigits.length === 10;
	const isMfaCodeComplete = mfaVerificationCode.replace(/\D/g, "").length === 6;
	const userMfaPhoneDigits = (user?.mfaPhone ?? "").replace(/\D/g, "");
	const isSavedMfaPhone = Boolean(
		userMfaPhoneDigits && userMfaPhoneDigits === mfaPhoneDigits,
	);

	const requestPasswordResetMutation = useRequestPasswordReset();
	const sendMfaVerificationCodeMutation = useSendMfaVerificationCode();
	const updateMfaMutation = useUpdateMfa();

	const hasError = !user && !isLoading;

	// Cooldown timer effect (password reset)
	useEffect(() => {
		if (cooldownRemaining <= 0) return;

		const timer = setInterval(() => {
			setCooldownRemaining((prev) => {
				if (prev <= 1) {
					return 0;
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [cooldownRemaining]);

	// MFA resend code cooldown timer
	useEffect(() => {
		if (mfaResendCooldown <= 0) return;

		const timer = setInterval(() => {
			setMfaResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
		}, 1000);

		return () => clearInterval(timer);
	}, [mfaResendCooldown]);

	// Prepopulate MFA phone when user has one and first enters edit mode
	const prevIsEditingMfaRef = useRef(false);
	useEffect(() => {
		const justEnteredEdit = isEditingMfa && !prevIsEditingMfaRef.current;
		prevIsEditingMfaRef.current = isEditingMfa;
		if (justEnteredEdit && user?.mfaPhone) {
			setMfaPhone(user.mfaPhone);
		}
	}, [isEditingMfa, user?.mfaPhone]);

	// Auto-submit when 6-digit code is entered
	useEffect(() => {
		if (mfaVerificationCode.replace(/\D/g, "").length === 6) {
			console.log("[MFA] Auto-submit verification code:", mfaVerificationCode);
		}
	}, [mfaVerificationCode]);

	// Handle password reset request
	const handleResetPassword = useCallback(async () => {
		if (!user?.id || cooldownRemaining > 0) return;

		try {
			await requestPasswordResetMutation.mutateAsync(user.id);
			setResetEmailSent(true);
			setCooldownRemaining(PASSWORD_RESET_COOLDOWN_SECONDS);

			// Hide the success message after 10 seconds
			setTimeout(() => {
				setResetEmailSent(false);
			}, 10000);
		} catch (error) {
			console.error("Failed to request password reset:", error);
		}
	}, [user?.id, cooldownRemaining, requestPasswordResetMutation]);

	if (isLoading)
		return (
			<div className="flex items-center justify-center text-center h-[calc(100vh-16rem)]">
				<LoadingIndicator type="line-simple" size="xl" supportingText />
			</div>
		);

	if (hasError)
		return (
			<div className="flex flex-col items-center justify-center h-full gap-4">
				<Illustration type="cloud" size="sm">
					<div className="bg-error-500 rounded-full p-3">
						<AlertCircle className="size-6" />
					</div>
				</Illustration>
				<div className="flex flex-col items-center justify-center pt-4">
					<p className="text-gray-900 font-semibold text-md">
						{t("admin.organizations.errors.errorLoadingPage")}
					</p>
					<div className="">
						<p className="text-gray-600 font-normal text-wrap max-w-xs text-sm mt-2">
							{t("admin.organizations.errors.errorLoadingContent")}
						</p>
					</div>
				</div>
			</div>
		);

	return (
		<div className="space-y-6">
			{/* Password Section */}
			{!isEditingMfa && (
				<div>
					<TabHeader
						title={t("userProfile.security.password.title")}
						descriptionText={t("userProfile.security.password.subtitle")}
					/>

					<div className="px-3 space-y-3">
						{!user?.usingFirstPartyAuth ? (
							// Third-Party Authentication (SSO) Message
							<p className="text-sm text-gray-600">
								{t("userProfile.security.password.thirdPartyAuthMessage")}
							</p>
						) : (
							// First-Party Authentication - Reset Password
							<>
								<p className="text-sm text-gray-600 text-wrap max-w-xs">
									{t("userProfile.security.password.resetInstructions")}
								</p>

								{/* Reset Password Button */}
								<Button
									color="secondary"
									size="sm"
									onClick={handleResetPassword}
									isLoading={requestPasswordResetMutation.isPending}
									disabled={cooldownRemaining > 0}
								>
									{cooldownRemaining > 0
										? t("userProfile.security.password.resetCooldown", {
												seconds: cooldownRemaining,
											})
										: t("userProfile.security.password.resetButton")}
								</Button>

								{/* Success Message */}
								{resetEmailSent && (
									<p className="text-sm text-gray-600">
										{t("userProfile.security.password.resetEmailSent", {
											email: user?.email,
										})}
									</p>
								)}

								{/* Error Message */}
								{requestPasswordResetMutation.isError && (
									<p className="text-sm text-error-600">
										{t("userProfile.security.password.resetError")}
									</p>
								)}
							</>
						)}
					</div>

					{/* Divider */}
					<div className="mt-6 border-t border-gray-200" />
				</div>
			)}

			{/* MFA Section */}
			<div>
				{isEditingMfa ? (
					<div className="space-y-6">
						<TabHeader
							title={t("userProfile.security.mfa.title")}
							descriptionText={t("userProfile.security.mfa.subtitle")}
						/>

						{/* Info: Email and Phone (SMS) methods */}
						<div className="flex gap-3 p-4 rounded-lg border border-gray-200 bg-info-50">
							<InfoCircle className="size-5 shrink-0 text-info-600 mt-0.5" />
							<div className="text-sm text-gray-700 space-y-1">
								<p>
									<strong>{t("userProfile.security.mfa.emailMethod")}</strong>:{" "}
									{t("userProfile.security.mfa.infoEmail")}
								</p>
								<p>
									<strong>
										{t("userProfile.security.mfa.phoneSmsMethod")}
									</strong>
									: {t("userProfile.security.mfa.infoPhone")}
								</p>
								<p>{t("userProfile.security.mfa.infoMultiple")}</p>
							</div>
						</div>

						<div className="space-y-2">
							<p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
								{t("userProfile.security.mfa.methodLabel")}
							</p>
						</div>

						{/* MFA Email (read-only, verified) */}
						<div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
							<div className="flex items-center justify-between gap-3">
								<div>
									<p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
										{t("userProfile.security.mfa.emailLabel")}
									</p>
									<p className="text-sm font-bold">{user?.email}</p>
								</div>
								<CheckCircle className="size-5 shrink-0 text-success-500" />
							</div>
						</div>

						{/* MFA Phone (SMS) - editable */}
						<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
							<div className="flex items-center justify-between gap-3">
								<div className="flex-1 min-w-0">
									<p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-2">
										{t("userProfile.security.mfa.phoneLabel")}
									</p>
									<Input
										value={mfaPhone}
										onChange={(e) => {
											const v =
												typeof e === "string"
													? e
													: ((e as React.ChangeEvent<HTMLInputElement>).target
															?.value ?? "");
											setMfaPhone(v);
										}}
										placeholder={t("userProfile.security.mfa.phonePlaceholder")}
										size="sm"
										isInvalid={mfaPhone.length > 0 && !isMfaPhoneValid}
									/>
								</div>
								{!isMfaPhoneValid ? (
									<XCircle className="size-5 shrink-0 text-error-500" />
								) : mfaCodeSent ? (
									<AlertCircle className="size-5 shrink-0 text-warning-500" />
								) : isSavedMfaPhone || isMfaPhoneValid ? (
									<CheckCircle className="size-5 shrink-0 text-success-500" />
								) : (
									<XCircle className="size-5 shrink-0 text-error-500" />
								)}
							</div>
							{isMfaPhoneValid && !mfaCodeSent && (
								<Button
									color="secondary"
									size="sm"
									onClick={async () => {
										if (!user?.id) return;
										try {
											await sendMfaVerificationCodeMutation.mutateAsync({
												userId: user.id,
												phoneNumber: mfaPhoneDigits,
											});
											setMfaCodeSent(true);
											setMfaResendCooldown(MFA_CODE_RESEND_COOLDOWN_SECONDS);
										} catch {
											// Error logged in mutation; optionally show toast
										}
									}}
									isLoading={sendMfaVerificationCodeMutation.isPending}
									disabled={sendMfaVerificationCodeMutation.isPending}
								>
									{t("userProfile.security.mfa.verifyPhoneNumber")}
								</Button>
							)}
							{mfaCodeSent && (
								<>
									<p className="text-sm text-gray-600">
										{t("userProfile.security.mfa.codeSentMessage", {
											phone: mfaPhoneDigits.replace(
												/(\d{3})(\d{3})(\d{4})/,
												"($1) $2-$3",
											),
										})}
									</p>
									{mfaSubmitError && (
										<p className="text-sm text-error-600" role="alert">
											{mfaSubmitError}
										</p>
									)}
									<div className="flex items-center gap-2 ">
										<Input
											value={mfaVerificationCode}
											onChange={(e) => {
												setMfaSubmitError(null);
												const v =
													typeof e === "string"
														? e
														: ((e as React.ChangeEvent<HTMLInputElement>).target
																?.value ?? "");
												setMfaVerificationCode(
													v.replace(/\D/g, "").slice(0, 6),
												);
											}}
											placeholder={t(
												"userProfile.security.mfa.enterCodePlaceholder",
											)}
											size="sm"
											// className="max-w-[160px]"
											maxLength={6}
											inputMode="numeric"
											aria-label={t(
												"userProfile.security.mfa.enterCodePlaceholder",
											)}
										/>
										<Button
											color="primary"
											size="sm"
											disabled={
												!isMfaCodeComplete || updateMfaMutation.isPending
											}
											isLoading={updateMfaMutation.isPending}
											onClick={async () => {
												if (!user?.id) return;
												setMfaSubmitError(null);
												try {
													await updateMfaMutation.mutateAsync({
														userId: user.id,
														data: {
															phone: mfaPhoneDigits,
															verificationCode: mfaVerificationCode.replace(
																/\D/g,
																"",
															),
														},
													});
													setMfaSubmitError(null);
													setIsEditingMfa(false);
													setMfaPhone("");
													setMfaCodeSent(false);
													setMfaVerificationCode("");
												} catch (err: unknown) {
													const data = (err as { data?: { errors?: string[] } })
														?.data;
													const messages = Array.isArray(data?.errors)
														? data.errors
														: [
																"Verification failed. Please check the code and try again.",
															];
													setMfaSubmitError(messages.join(" "));
												}
											}}
										>
											{t("userProfile.security.mfa.submitCode")}
										</Button>
										<ButtonUtility
											icon={RefreshCw01}
											color="secondary"
											size="sm"
											isDisabled={
												mfaResendCooldown > 0 ||
												sendMfaVerificationCodeMutation.isPending
											}
											onClick={async () => {
												if (!user?.id) return;
												try {
													await sendMfaVerificationCodeMutation.mutateAsync({
														userId: user.id,
														phoneNumber: mfaPhoneDigits,
													});
													setMfaResendCooldown(
														MFA_CODE_RESEND_COOLDOWN_SECONDS,
													);
												} catch {
													// Error logged in mutation
												}
											}}
											tooltip={t("userProfile.security.mfa.resend")}
										/>
									</div>
								</>
							)}
						</div>
					</div>
				) : (
					<>
						<TabHeader
							title={t("userProfile.security.mfa.title")}
							descriptionText={t("userProfile.security.mfa.subtitle")}
							actions={
								user?.usingFirstPartyAuth && (
									<ButtonUtility
										icon={Edit05}
										color="tertiary"
										size="sm"
										onClick={() => setIsEditingMfa(true)}
										tooltip={t("common.actions.edit")}
									/>
								)
							}
						/>

						{!user?.usingFirstPartyAuth ? (
							// Third-Party Authentication (SSO) Message
							<p className="text-sm text-gray-600 px-3">
								{t("userProfile.security.mfa.thirdPartyAuthMessage")}
							</p>
						) : (
							<div className="px-3">
								<div className="p-4 bg-gray-50 rounded-lg space-y-4">
									{/* MFA Status + managed-by-org message */}
									<div>
										<p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
											{t("userProfile.security.mfa.statusLabel")}
										</p>
										<Badge
											color={mfaEnabled ? "success" : "gray"}
											size="sm"
											className={
												!mfaEnabled
													? "!bg-gray-500 !text-white ring-gray-500"
													: undefined
											}
										>
											{mfaEnabled
												? t("userProfile.security.mfa.enabled")
												: t("userProfile.security.mfa.disabled")}
										</Badge>
										<p className="text-sm text-gray-600 mt-1">
											{t("userProfile.security.mfa.managedByOrg")}
										</p>
									</div>

									{/* MFA Email */}
									<div className="flex items-center gap-3">
										<Mail01 className="size-5 shrink-0 text-gray-400" />
										<div className="min-w-0 flex-1">
											<p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">
												{t("userProfile.security.mfa.emailLabel")}
											</p>
											<p className="text-sm font-medium text-gray-900">
												{user?.email ?? "—"}
											</p>
										</div>
									</div>

									{/* MFA Phone (SMS) */}
									<div className="flex items-center gap-3">
										<Phone className="size-5 shrink-0 text-gray-400" />
										<div className="min-w-0 flex-1">
											<p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">
												{t("userProfile.security.mfa.phoneLabel")}
											</p>
											<p className="text-sm font-medium text-gray-900">
												{user?.mfaPhone || "—"}
											</p>
										</div>
									</div>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
